import { notification } from 'antd';
import storage from 'local-storage-fallback';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { GIFT_CODE_ENCRYPTION_PASSWORD_KEY_PREFIX } from '@/App/constants';
import GachaForm, { FormValues } from '@/Gacha/components/GachaForm';
import { createApiClientWithToken } from '@/service/PreRegistrationApiFactory';
import { GiftCodeCrypto } from '@/service/giftCodeCrypto';
import { convertPercentageToRate } from '@/service/numericOperation';

const CreateGacha: React.FC<{}> = () => {
  const api = createApiClientWithToken();
  const history = useHistory();

  const onFinish = async (values: FormValues) => {
    const crypto = new GiftCodeCrypto(values.encryptionPassword);
    const { encrypted, iv } = crypto.encryptString(values.name);

    const createGachaRes = await api
      .createGacha({
        body: {
          name: values.name,
          upperTimes: values.upperTimes,
          winningRate: convertPercentageToRate(values.winningPercentage),
          iosDownloadUrl: values.iosDownloadUrl,
          androidDownloadUrl: values.androidDownloadUrl,
          canRetry: values.canRetry,
          retryInterval: values.retryInterval ? values.retryInterval * 60 * 60 : undefined,
          startTime: values.validPeriod ? values.validPeriod[0].toDate() : undefined,
          endTime: values.validPeriod ? values.validPeriod[1].toDate() : undefined,
          gameTitle: values.gameTitle,
          developerName: values.developerName,
          iconImageUrl: values.iconImageUrl,
          giftDetail: values.giftDetail,
          gameIntroduction: values.gameIntroduction,
          articleUrl: values.articleUrl,
          gameReleaseTime: values.gameReleaseTime,
          officialSiteUrl: values.officialSiteUrl,
          encryptName: encrypted,
          iv,
        },
      })
      .catch(() => {
        notification.error({
          message: 'ガチャの作成に失敗しました。',
          description: `作成しようとしたガチャ名: ${values.name}`,
        });
      });

    if (!createGachaRes || !createGachaRes.id) {
      return;
    }

    const editReleaseEmailTemplateRes = await api
      .editReleaseEmailTemplate(createGachaRes.id, {
        body: {
          title: values.releaseNotificationEmailTitle,
          body: values.releaseNotificationEmailBody,
          htmlBody: values.releaseNotificationEmailHtmlBody ?? '',
        },
      })
      .catch(() => {
        notification.error({
          message: 'リリース通知の本文保存に失敗しました。',
          description: `作成しようとしたガチャ名: ${values.name}`,
        });
      });

    const editRegistrationEmailTemplateRes = await api
      .editRegistrationEmailTemplate(createGachaRes.id, {
        body: {
          title: values.registrationNotificationEmailTitle,
          body: values.registrationNotificationEmailBody,
          htmlBody: values.registrationNotificationEmailHtmlBody ?? '',
        },
      })
      .catch(() => {
        notification.error({
          message: '登録完了メールの本文保存に失敗しました。',
          description: `作成しようとしたガチャ名: ${values.name}`,
        });
      });

    if (!editRegistrationEmailTemplateRes || !editReleaseEmailTemplateRes) {
      return;
    }

    const giftCodeCryptoPasswordKey = `${GIFT_CODE_ENCRYPTION_PASSWORD_KEY_PREFIX}${values.name}`;
    storage.setItem(giftCodeCryptoPasswordKey, values.encryptionPassword);

    notification.success({
      message: 'ガチャの作成に成功しました',
      description: `ガチャ名: ${values.name}`,
    });
    history.push(`/gacha/${createGachaRes.id}`);
  };

  return <div>{GachaForm({ onFinish })}</div>;
};

export default CreateGacha;
